import React from "react";
/**
 * ProductSetCell - Manual Product Set ID input for Ad Sheet
 *
 * Users enter a raw Product Set ID (digits only).
 * When no sheet-level catalog is set, shows "Select catalog first".
 *
 * TODO: Restore dropdown via useCachedProductSets when
 * catalog_management Advanced Access is approved.
 */

import { useState, useEffect } from "react";
import { X } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

interface ProductSetCellValue {
  catalogId: string | null;
  catalogName: string | null;
  productSetId: string | null;
  productSetName: string | null;
}

interface ProductSetCellProps {
  value: ProductSetCellValue;
  onChange: (value: ProductSetCellValue) => void;
  adFormat: string | null;
  userId?: string;
  adAccountId?: string;
  sheetCatalogId?: string | null;
}

type CellMode = "visible" | "hidden" | "required";

function getCellMode(adFormat: string | null): CellMode {
  switch (adFormat) {
    case "Single Image or Video":
      return "visible";
    case "Collection":
    case "Catalog":
      return "required";
    case "Carousel":
    case "Flexible":
      return "hidden";
    default:
      return "visible";
  }
}

export function ProductSetCell({
  value,
  onChange,
  adFormat,
  userId,
  adAccountId,
  sheetCatalogId,
}: ProductSetCellProps) {
  const mode = getCellMode(adFormat);
  const effectiveCatalogId = sheetCatalogId || value.catalogId;

  const [localValue, setLocalValue] = useState(value.productSetId || "");

  // Sync from parent when value changes externally
  useEffect(() => {
    setLocalValue(value.productSetId || "");
  }, [value.productSetId]);

  const handleBlur = () => {
    const trimmed = localValue.trim();
    // Allow empty or digits-only
    if (trimmed && !/^\d+$/.test(trimmed)) return;
    onChange({
      catalogId: effectiveCatalogId || null,
      catalogName: value.catalogName,
      productSetId: trimmed || null,
      productSetName: trimmed || null, // Same value since we only have the ID
    });
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    setLocalValue("");
    onChange({
      catalogId: null,
      catalogName: null,
      productSetId: null,
      productSetName: null,
    });
  };

  // Hidden mode
  if (mode === "hidden") {
    return (
      <div className="h-full w-full bg-muted/50 flex items-center justify-center">
        <span className="text-xs text-muted-foreground/50">N/A</span>
      </div>
    );
  }

  // No catalog selected at sheet level
  if (!effectiveCatalogId) {
    return (
      <div className="flex items-center justify-center p-1">
        <span className="text-xs text-muted-foreground/60">Select catalog first</span>
      </div>
    );
  }

  const isRequired = mode === "required";
  const isMissingRequired = isRequired && !value.productSetId;

  return (
    <div
      className={cn(
        "flex flex-col gap-0.5 p-1 rounded relative",
        isMissingRequired && "ring-1 ring-red-500 ring-inset",
      )}
    >
      {/* Clear button */}
      {value.productSetId && (
        <Button
          variant="ghost"
          size="icon"
          className="absolute -top-1 -right-1 h-4 w-4 rounded-full bg-muted hover:bg-destructive/20 z-10"
          onClick={handleClear}
        >
          <X className="h-3 w-3" />
        </Button>
      )}

      <Input
        type="text"
        placeholder="Product Set ID"
        value={localValue}
        onChange={(e) => setLocalValue(e.target.value)}
        onBlur={handleBlur}
        onKeyDown={(e) => {
          if (e.key === "Enter") (e.target as HTMLInputElement).blur();
        }}
        className="h-7 text-xs px-2"
      />
      <a
        href="https://business.facebook.com/commerce/"
        target="_blank"
        rel="noopener noreferrer"
        className="text-[10px] text-muted-foreground hover:underline leading-tight"
      >
        Find in Commerce Manager → Product Sets
      </a>
    </div>
  );
}
